<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> Back </BaseButton>

        <div v-if="loading" class="body-small text-ink-muted">Loading…</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="campaign">
            <header class="flex items-start justify-between gap-4 flex-wrap">
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="title-subsection text-ink">{{ campaign.name }}</h2>
                    <p class="body-small text-ink-muted">
                        {{ campaign.template.name }} ·
                        <span class="body-small-bold text-ink">{{ campaign.cycle_count }} participants</span>
                        · {{ campaign.submitted_response_count }} responses submitted
                    </p>
                    <p v-if="report?.generated_at" class="details text-ink-muted">
                        Report Generated {{ formatDate(report.generated_at, "short") }}
                        <span v-if="newSubmissionsSinceReport > 0" class="text-ink body-small-bold"> · {{ newSubmissionsSinceReport }} new submissions since last report </span>
                    </p>
                </div>
                <div class="flex items-center gap-2 flex-wrap shrink-0">
                    <BaseButton theme="neutral" size="sm" :disabled="downloading" @click="onDownloadPdf">
                        <i class="bi bi-download" aria-hidden="true"></i>
                        {{ downloading ? "…" : "Download PDF" }}
                    </BaseButton>
                    <BaseTooltip :disabled="campaignClosed">
                        <template #trigger>
                            <!-- A disabled button swallows hover, so the span carries the tooltip. -->
                            <span class="inline-flex">
                                <BaseButton theme="neutral" size="sm" :disabled="downloadingCsv || !campaignClosed" @click="onDownloadCsv">
                                    <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                                    {{ downloadingCsv ? "…" : "Download CSV" }}
                                </BaseButton>
                            </span>
                        </template>
                        <template #content>{{ t("reflectionsAdmin.results.csvAvailableWhenClosed") }}</template>
                    </BaseTooltip>
                    <BaseButton theme="primary" size="sm" :disabled="regenerating" @click="onRegenerate">
                        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                        {{ regenerating ? "Queued…" : "Regenerate report" }}
                    </BaseButton>
                </div>
            </header>

            <BaseNotification v-if="regenStatus" :theme="regenStatus.theme" :text="regenStatus.text" @dismiss="regenStatus = null" />

            <HorizontalDivider as="hr" spacing="none" />

            <BaseCard v-if="!report" padding="lg" class="text-center">
                <p class="body-small text-ink-muted">No report generated yet. Click "Regenerate report" to generate one.</p>
            </BaseCard>

            <template v-else>
                <ExecutiveSummarySection v-if="report.executive_summary" :summary="report.executive_summary" />

                <SelfCategoryScoresSection
                    v-if="aggregates && hasCategoryScores"
                    :sections="filteredSections"
                    :max-score="maxScore"
                    intro="Rolled-up averages across each section of the assessment." />

                <section v-if="availableFilters.length > 0" class="flex flex-col gap-3">
                    <BaseRadioGroup
                        v-model="relationshipFilter"
                        label="Filter by rater"
                        orientation="horizontal"
                        :options="availableFilters.map((opt) => ({ value: opt.value, label: `${opt.label}${opt.count !== null ? ` (${opt.count})` : ''}` }))" />
                    <p v-if="relationshipFilter !== 'all'" class="details text-ink-muted">
                        Distributions and averages reflect the selected raters only. Executive summary and themes always reflect the full set.
                    </p>
                </section>

                <section v-for="panel in sectionPanels" :key="panel.section.section_id" class="flex flex-col gap-2.5">
                    <p class="body-small-semibold text-ink">Section Insight</p>
                    <CampSectionInsight :headline="panel.section.insight?.headline || undefined" :chart-rows="panel.chartRows" :insight="panel.section.insight ?? null" />
                </section>

                <section v-if="openTextGroups.length > 0" class="flex flex-col gap-3">
                    <p class="body-small-semibold text-ink">Open-text response</p>
                    <CampOpenTextSection
                        v-for="group in openTextGroups"
                        :key="group.questionId"
                        :title="group.title"
                        :response-count="group.responseCount"
                        :themes="group.themes" />
                </section>

                <section v-if="detailedRows.length > 0" class="flex flex-col gap-2.5">
                    <p class="body-small-semibold text-ink">Detailed breakdown</p>
                    <BaseCard padding="lg" class="flex flex-col gap-10">
                        <CampSegmentedBar v-for="row in detailedRows" :key="row.key" :label="row.label" :distribution="row.distribution" :avg="row.avg" />
                        <CampLegend />
                    </BaseCard>
                </section>
            </template>

            <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { useTimeoutPoll } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import type { CampaignAdminView, CampaignReportPayload, CampaignResultsResponse } from "~vue/api/generated/reflections";
import ExecutiveSummarySection from "~vue/AssessmentResults/ExecutiveSummarySection.vue";
import type { Aggregates, Cut, PerQuestion, SectionAggregate } from "~vue/AssessmentResults/types";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import CampLegend from "~vue/pages/reflections/CampLegend.vue";
import CampOpenTextSection from "~vue/pages/reflections/CampOpenTextSection.vue";
import CampSectionInsight from "~vue/pages/reflections/CampSectionInsight.vue";
import CampSegmentedBar from "~vue/pages/reflections/CampSegmentedBar.vue";
import SelfCategoryScoresSection from "~vue/pages/reflections/SelfCategoryScoresSection.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { resolveQuestionName } from "~vue/utils/assessmentValidation";
import { logUserInteraction } from "~vue/utils/logUtils";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    campaignId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "back"): void }>();
const { t } = useI18n();

const campaign = ref<CampaignAdminView | null>(null);
const report = ref<CampaignReportPayload | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const regenerating = ref(false);
const downloading = ref(false);
const downloadingCsv = ref(false);
const regenStatus = ref<{ theme: "success" | "info" | "error"; text: string } | null>(null);
const relationshipFilter = ref<string>("all");
// Raw rows could be diffed against the Raters tab while responses still arrive.
const campaignClosed = computed(() => campaign.value?.state === "closed");

const campaignDemoActions = computed(() => {
    const currentCampaign = campaign.value;
    return currentCampaign && currentCampaign.state !== "closed" ? [{ id: currentCampaign.id, label: currentCampaign.name }] : [];
});

const aggregates = computed<Aggregates | null>(() => report.value?.aggregates ?? null);
const hasCategoryScores = computed(() => (aggregates.value?.sections ?? []).some((s) => s.avg !== null));

const maxScore = computed(() => {
    const first = aggregates.value?.per_question?.find((q) => q.is_score);
    return first?.max_score ?? first?.distribution?.length ?? 5;
});

const availableFilters = computed<{ value: string; label: string; count: number | null }[]>(() => {
    const sections = aggregates.value?.sections ?? [];
    const totals = new Map<string, { label: string; count: number }>();
    for (const s of sections) {
        for (const cut of s.cuts ?? []) {
            const cur = totals.get(cut.relationship);
            if (cur) {
                cur.count += cut.count;
            } else {
                totals.set(cut.relationship, { label: cut.label, count: cut.count });
            }
        }
    }
    const opts: { value: string; label: string; count: number | null }[] = [{ value: "all", label: "All", count: null }];
    for (const [value, { label, count }] of totals) {
        if (count > 0) {
            opts.push({ value, label, count });
        }
    }
    return opts.length > 1 ? opts : [];
});

function applyCutToQuestion(q: PerQuestion): PerQuestion {
    if (relationshipFilter.value === "all") {
        return q;
    }
    const cut: Cut | undefined = q.cuts?.find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...q, all_avg: null, all_count: 0, distribution: (q.distribution ?? []).map(() => 0) };
    }
    return { ...q, all_avg: cut.avg, all_count: cut.count, distribution: cut.distribution };
}

function applyCutToSection(s: SectionAggregate): SectionAggregate {
    if (relationshipFilter.value === "all") {
        return s;
    }
    const cut = (s.cuts ?? []).find((c) => c.relationship === relationshipFilter.value);
    if (!cut || !cut.distribution) {
        return { ...s, avg: null, count: 0, distribution: (s.distribution ?? []).map(() => 0) };
    }
    return { ...s, avg: cut.avg, count: cut.count, distribution: cut.distribution };
}

// Campaign questions use the `{{ name }}` placeholder because the templates
// power both 360s and self reflections. A campaign has many subjects, so
// there's no single first name to substitute — use a generic noun.
function labelFor(q: PerQuestion): string {
    return resolveQuestionName(q.text || q.title, "the participant");
}

const filteredSections = computed<SectionAggregate[]>(() => (aggregates.value?.sections ?? []).map(applyCutToSection));
const filteredScoreQuestions = computed<PerQuestion[]>(() => (aggregates.value?.per_question ?? []).filter((q) => q.is_score).map(applyCutToQuestion));

interface SectionPanel {
    section: SectionAggregate;
    chartRows: { key: string; label: string; distribution: number[]; avg: number | null }[];
}

// Top-level sections drive the "Section Insight" cards. Each card's chart rows
// are the section's score questions (rolled up with the current rater filter
// applied). If a section has no scored questions, the chart is omitted and only
// the LLM insight renders.
const sectionPanels = computed<SectionPanel[]>(() => {
    const sections = filteredSections.value;
    if (sections.length === 0) {
        return [];
    }
    const topLevel = sections.filter((s) => s.depth === 0).sort((a, b) => a.order - b.order);

    return topLevel.map((section) => {
        const subsectionIds = new Set<string>([section.section_id]);
        for (const s of sections) {
            if (s.parent_section_id === section.section_id) {
                subsectionIds.add(s.section_id);
            }
        }
        const chartRows = filteredScoreQuestions.value
            .filter((q) => q.section_id && subsectionIds.has(q.section_id))
            .map((q) => ({
                key: q.question_id,
                label: labelFor(q),
                distribution: q.distribution ?? [],
                avg: q.all_avg,
            }));
        return { section, chartRows };
    });
});

// Detailed breakdown shows every score question across the whole campaign
// with the filter applied — same segmented bars, no section grouping.
const detailedRows = computed(() =>
    filteredScoreQuestions.value.map((q) => ({
        key: q.question_id,
        label: labelFor(q),
        distribution: q.distribution ?? [],
        avg: q.all_avg,
    })),
);

// Open-text: group text questions by section, then merge their themes. Falls
// back to top-level `report.themes` for campaigns whose text questions don't
// carry synthesized themes.
interface OpenTextGroup {
    questionId: string;
    title: string;
    responseCount: number;
    themes: { title: string; summary?: string | null }[];
}

const openTextGroups = computed<OpenTextGroup[]>(() => {
    const groups: OpenTextGroup[] = [];
    const textQuestions = aggregates.value?.per_question?.filter((q) => q.is_text) ?? [];
    for (const q of textQuestions) {
        if (!q.open_text_synthesis && !(report.value?.themes?.length ?? 0)) {
            continue;
        }
        // Themes are report-level today; anchor them under the first text
        // question so the mockup's "clustered into N themes" line renders.
        const themes = q.open_text_synthesis
            ? [{ title: "Synthesis", summary: q.open_text_synthesis }]
            : (report.value?.themes ?? []).map((t) => ({ title: t.title, summary: t.summary }));
        groups.push({
            questionId: q.question_id,
            title: labelFor(q),
            responseCount: q.open_text_count ?? q.all_count ?? 0,
            themes,
        });
    }
    // If no per-question themes but campaign-level themes exist, show them
    // once as a general open-text summary.
    if (groups.length === 0 && (report.value?.themes?.length ?? 0) > 0) {
        groups.push({
            questionId: "themes",
            title: "Open-text themes",
            responseCount: (report.value?.themes ?? []).reduce((sum, t) => sum + ((t as { count?: number }).count ?? 0), 0),
            themes: (report.value?.themes ?? []).map((t) => ({ title: t.title, summary: t.summary })),
        });
    }
    return groups;
});

const newSubmissionsSinceReport = computed(() => {
    if (!campaign.value || !report.value) {
        return 0;
    }
    return Math.max(0, (campaign.value.submitted_response_count ?? 0) - (report.value.response_count_at_generation ?? 0));
});

async function fetchResults(): Promise<CampaignResultsResponse | null> {
    const { data } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/results`).json<CampaignResultsResponse>();
    return data.value ?? null;
}

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_PDF_DOWNLOAD_CLICKED, { campaign_id: props.campaignId });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaignId}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_report.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onDownloadCsv() {
    if (downloadingCsv.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_CSV_DOWNLOAD_CLICKED, { campaign_id: props.campaignId });
    downloadingCsv.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaignId}/export-csv`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_raw_data.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloadingCsv.value = false;
    }
}

// Regeneration is async on the server (Celery task); we POST once then poll
// /results until `generated_at` advances. 36s budget matches the celery task's
// typical runtime; if the report takes longer we tell the user to refresh.
const REGEN_TIMEOUT_MS = 36_000;
let regenStartedAt = 0;
let regenBaselineAt: string | null = null;
const { resume: startRegenPoll, pause: stopRegenPoll } = useTimeoutPoll(
    async () => {
        if (Date.now() - regenStartedAt > REGEN_TIMEOUT_MS) {
            stopRegenPoll();
            regenerating.value = false;
            regenStatus.value = { theme: "info", text: "Regeneration is taking longer than expected. Refresh in a moment to see the updated report." };
            return;
        }
        const latest = await fetchResults();
        const newGenerated = latest?.report?.generated_at ?? null;
        if (newGenerated && newGenerated !== regenBaselineAt) {
            stopRegenPoll();
            campaign.value = latest!.campaign;
            report.value = latest!.report ?? null;
            regenerating.value = false;
            regenStatus.value = { theme: "success", text: `Report regenerated (${((Date.now() - regenStartedAt) / 1000).toFixed(0)}s).` };
        }
    },
    2000,
    { immediate: false },
);

onUnmounted(() => stopRegenPoll());

async function onRegenerate() {
    if (regenerating.value) {
        return;
    }
    regenerating.value = true;
    regenStatus.value = { theme: "info", text: "Regenerating report — this usually takes a few seconds…" };
    regenBaselineAt = report.value?.generated_at ?? null;
    const { error: postError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/regenerate`, { method: "POST" }).json();
    if (postError.value) {
        regenerating.value = false;
        regenStatus.value = { theme: "error", text: "Could not queue regeneration. Please try again." };
        return;
    }
    regenStartedAt = Date.now();
    startRegenPoll();
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/results`).json<CampaignResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || "Could not load campaign results.";
        return;
    }
    campaign.value = data.value?.campaign ?? null;
    report.value = data.value?.report ?? null;
});
</script>
